import React, { useEffect, useState } from "react";
import { Timer } from "lucide-react";

/**
 * NextClueChip — shows "Next clue in Xm" on live drops when at least one
 * clue is still locked. Computes locally from drop.launch_time and each
 * clue's release_offset_minutes so we can tick without hitting the API.
 *
 * Renders nothing when:
 *  - drop is not live
 *  - no launch_time
 *  - no clues
 *  - all clues are already unlocked (elapsed >= max offset)
 */
export default function NextClueChip({ drop, className = "", size = "sm" }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  if (!drop || drop.status !== "live" || !drop.launch_time) return null;
  const clues = Array.isArray(drop.clues) ? drop.clues : [];
  if (clues.length === 0) return null;

  let launchMs;
  try {
    launchMs = new Date(drop.launch_time).getTime();
  } catch {
    return null;
  }
  if (!Number.isFinite(launchMs)) return null;

  const elapsedMin = (now - launchMs) / 60_000;
  const nextOffset = clues
    .map((c) => Number(c?.release_offset_minutes) || 0)
    .filter((off) => off > elapsedMin)
    .sort((a, b) => a - b)[0];

  if (nextOffset === undefined) return null; // all clues unlocked

  const remainMin = Math.max(0, nextOffset - elapsedMin);
  const label =
    remainMin < 1
      ? "Next clue in <1m"
      : remainMin < 60
      ? `Next clue in ${Math.ceil(remainMin)}m`
      : `Next clue in ${Math.floor(remainMin / 60)}h ${Math.ceil(remainMin % 60)}m`;

  const padding = size === "lg" ? "px-3 py-1" : "px-2 py-[3px]";
  const textSize = size === "lg" ? "text-xs" : "text-[10px]";
  const iconSize = size === "lg" ? 12 : 10;

  return (
    <span
      data-testid={`next-clue-chip-${drop.id || "detail"}`}
      className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest rounded-full border ${padding} ${textSize} ${className}`}
      style={{
        color: "#D7A62A",
        borderColor: "rgba(215,166,42,0.6)",
        background: "rgba(215,166,42,0.08)",
      }}
      title={`Launched ${new Date(drop.launch_time).toLocaleString()}`}
    >
      <Timer size={iconSize} />
      {label}
    </span>
  );
}
